import React from 'react';
import '../styles/services.css';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import {
  FaLaptopCode, FaIndustry, FaCheckCircle, FaArrowRight,
  FaChevronRight, FaRecycle, FaShieldAlt, FaTruck, FaClock,
  FaLeaf, FaLock, FaFileAlt
} from 'react-icons/fa';

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  show:   { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22,1,0.36,1] } },
};
const fadeLeft  = { hidden:{opacity:0,x:-55}, show:{opacity:1,x:0,transition:{duration:0.6,ease:[0.22,1,0.36,1]}} };
const fadeRight = { hidden:{opacity:0,x:55},  show:{opacity:1,x:0,transition:{duration:0.6,ease:[0.22,1,0.36,1]}} };
const stagger   = { hidden:{}, show:{ transition:{ staggerChildren:0.1, delayChildren:0.05 } } };

function Section({ children, className='' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once:true, margin:'-80px' });
  return (
    <motion.section ref={ref} className={className}
      variants={stagger} initial="hidden" animate={inView?'show':'hidden'}>
      {children}
    </motion.section>
  );
}

const cards = [
  {
    img: '/Assets/kmkimg16.jpg',
    icon: <FaLaptopCode />, tag: 'IT Assets',
    title: 'IT Assets Disposition',
    desc: 'Managing outdated or unused IT assets can be a challenge. We provide secure pickup, certified data destruction, and maximum value recovery for all IT equipment — ensuring regulatory compliance throughout.',
    points: [
      { icon: <FaLock />,     text: 'Secure Data Erasure — all sensitive information permanently wiped' },
      { icon: <FaRecycle />,  text: 'IT Asset Recovery — refurbishing and reselling usable equipment' },
      { icon: <FaLeaf />,     text: 'Responsible Recycling — eco-friendly disposal of non-reusable parts' },
      { icon: <FaLaptopCode />,text:'Servers, Laptops, Desktops & Peripherals' },
    ],
  },
  {
    img: '/Assets/imgkmk13.jpg',
    icon: <FaIndustry />, tag: 'All Metals',
    title: 'All Metal Disposition',
    desc: 'KMK Industries is your trusted partner for all metal disposition. We handle a wide variety of ferrous and non-ferrous metals, ensuring they are recycled and repurposed in the most eco-friendly way possible.',
    points: [
      { icon: <FaIndustry />, text: 'Ferrous & Non-Ferrous Metals — steel, aluminium, copper & more' },
      { icon: <FaTruck />,    text: 'Scrap Metal Collection — convenient on-site pickup & transport' },
      { icon: <FaLeaf />,     text: 'Sustainable Practices — responsible methods to minimise landfill' },
      { icon: <FaFileAlt />,  text: 'Bulk & Commercial Pickup with full documentation' },
    ],
  },
];

const approach = [
  { icon: <FaTruck />,     title: 'Convenient Pickup',  text: 'Scheduled collection from your premises — no logistics hassle.' },
  { icon: <FaShieldAlt />, title: 'Certified Process',  text: 'Documented, compliant handling for every asset we receive.' },
  { icon: <FaRecycle />,   title: 'Eco-Friendly',       text: 'Maximum recovery, minimal landfill — responsible by default.' },
];

const Services = () => {
  const navigate = useNavigate();

  return (
    <div className="services-page">

      {/* ── Hero ── */}
      <section className="page-hero" style={{ backgroundImage:"url('/Assets/kmkimg14.jpg')" }}>
        <div className="ph-overlay" />
        <div className="ph-particles" aria-hidden="true">
          {[...Array(5)].map((_,i)=><span key={i} className={`ph-part ph-part--${i+1}`}/>)}
        </div>
        <span className="ph-glow-a" /><span className="ph-glow-b" />
        <div className="container ph-inner">
          <motion.div initial="hidden" animate="show" variants={stagger}>
            <motion.nav className="ph-breadcrumb" variants={fadeUp}>
              <Link to="/">Home</Link>
              <FaChevronRight className="ph-crumb-sep" />
              <span>Services</span>
            </motion.nav>
            <motion.p className="ph-eyebrow" variants={fadeUp}>What We Offer</motion.p>
            <motion.h1 className="ph-title" variants={fadeUp}>
              Our <span className="ph-title-accent">Services</span>
            </motion.h1>
            <motion.p className="ph-sub" variants={fadeUp}>
              Secure, certified, and eco-friendly recycling solutions for IT assets and all metals —
              built for businesses of every size.
            </motion.p>
            <motion.div className="ph-actions" variants={fadeUp}>
              <button className="ph-btn-primary" onClick={()=>navigate('/contact')}>
                Get Free Quote <FaArrowRight />
              </button>
              <button className="ph-btn-outline" onClick={()=>navigate('/about')}>
                Learn More
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Intro ── */}
      <Section className="services-body">
        <div className="container">
          <motion.div className="services-intro" variants={fadeUp}>
            <p className="eyebrow">Certified &amp; Trusted</p>
            <h2>End-to-End Recycling <span className="text-green">Solutions</span></h2>
            <p className="sub">
              From secure data destruction to bulk metal pickup, we handle every step responsibly —
              so you can recycle with complete confidence.
            </p>
          </motion.div>

          {/* Service cards */}
          <div className="svc-grid">
            {cards.map((c, i) => (
              <motion.article
                className="svc-page-card"
                key={i}
                variants={i===0 ? fadeLeft : fadeRight}
                whileHover={{ y:-8, transition:{duration:0.25} }}
              >
                <div className="svc-page-img-wrap">
                  <img src={c.img} alt={c.title} className="svc-page-img" loading="lazy" />
                  <span className="svc-page-tag">{c.icon} {c.tag}</span>
                  <div className="svc-page-shade" />
                </div>
                <div className="svc-page-body">
                  <h3>{c.title}</h3>
                  <p>{c.desc}</p>
                  <ul className="svc-page-list">
                    {c.points.map((pt, j) => (
                      <motion.li
                        key={j}
                        initial={{ opacity:0, x:-20 }}
                        whileInView={{ opacity:1, x:0 }}
                        viewport={{ once:true }}
                        transition={{ delay: j*0.08, duration:0.4 }}
                      >
                        <FaCheckCircle /> {pt.text}
                      </motion.li>
                    ))}
                  </ul>
                  <a href="/contact" className="svc-page-btn">
                    Request Pickup <FaArrowRight />
                  </a>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Approach band */}
          <motion.div
            className="svc-approach"
            initial={{ opacity:0, y:40 }}
            whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }}
            transition={{ duration:0.6 }}
          >
            {approach.map((a, i) => (
              <motion.div
                className="svc-approach-item"
                key={i}
                whileHover={{ y:-6, transition:{duration:0.2} }}
              >
                <span className="svc-approach-icon">{a.icon}</span>
                <h4>{a.title}</h4>
                <p>{a.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* ── CTA ── */}
      <section className="page-cta">
        <div className="container cta-inner">
          <motion.div
            className="cta-text"
            initial={{ opacity:0, y:40 }}
            whileInView={{ opacity:1, y:0 }}
            viewport={{ once:true }}
            transition={{ duration:0.65 }}
          >
            <motion.span
              className="cta-icon"
              animate={{ rotate:[0,10,-10,8,-8,0] }}
              transition={{ duration:2, repeat:Infinity, repeatDelay:3 }}
            >
              <FaRecycle />
            </motion.span>
            <h2>Ready to Dispose <span className="text-green">Responsibly?</span></h2>
            <p>Get a free, no-obligation quote for your IT assets or metal scrap today.</p>
            <button className="ph-btn-primary" onClick={()=>navigate('/contact')}>
              Get Free Quote <FaArrowRight />
            </button>
            <div className="cta-trust">
              <span><FaCheckCircle /> No obligation</span>
              <span><FaShieldAlt /> 100% secure</span>
              <span><FaClock /> Quick response</span>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Services;
